import { useMutation } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { BulkDeactivateResult, MatchPeopleResult, PersonQuery } from '@up2d8/contracts';
import { parseExitList } from '../../shared/exit-list';
import { useSession } from '../../shared/session';
import { useProblemText } from '../../shared/use-problem-text';
import { AdminIcon, Btn, Field, Input, Modal, Note, Radio, Stack, Textarea } from '../admin-kit';

/**
 * Wat de medewerkerslijst sinds de testersfeedback kan (FBK-4): sorteren per kolom, en meerdere
 * mensen tegelijk uit dienst zetten - aangevinkt, of uit een uitdienstlijst. Uit dienst is
 * dezelfde uitdienst als voor één persoon: het account gaat uit, er wordt niets gewist.
 */

export type SortColumn = NonNullable<PersonQuery['sort']>;
export type SortDirection = 'asc' | 'desc';

/** Een kolomkop die sorteert; nog eens klikken keert de richting om. */
export function SortableTh({
  label,
  column,
  sort,
  direction,
  onSort,
}: {
  label: string;
  column: SortColumn;
  sort: SortColumn;
  direction: SortDirection;
  onSort: (column: SortColumn, direction: SortDirection) => void;
}) {
  const active = sort === column;
  return (
    <th aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        className="a-sort"
        data-testid={`people-sort-${column}`}
        onClick={() => onSort(column, active && direction === 'asc' ? 'desc' : 'asc')}
      >
        {label}
        <AdminIcon
          name={active && direction === 'desc' ? 'chevronDown' : 'chevronUp'}
          size={12}
          style={{ opacity: active ? 1 : 0.35 }}
        />
      </button>
    </th>
  );
}

/** Het moment en de reden, gedeeld door beide vensters. */
function useExitFields() {
  const { t } = useTranslation();
  const [when, setWhen] = useState<'now' | 'date'>('now');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const effective = when === 'now' ? 'now' : date;
  const valid = when === 'now' || /^\d{4}-\d{2}-\d{2}$/.test(date);

  const element = (
    <>
      <Field label={t('peopleBulk.when')}>
        <Stack>
          <Radio
            name="exit-when"
            value="now"
            checked={when === 'now'}
            onChange={() => setWhen('now')}
            label={t('peopleBulk.now')}
          />
          <Radio
            name="exit-when"
            value="date"
            checked={when === 'date'}
            onChange={() => setWhen('date')}
            label={t('peopleBulk.onDate')}
          />
        </Stack>
      </Field>
      {when === 'date' ? (
        <Field label={t('peopleBulk.date')}>
          {(props) => (
            <Input
              {...props}
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          )}
        </Field>
      ) : null}
      <Field label={t('peopleBulk.reason')} optional>
        {(props) => (
          <Input {...props} value={reason} onChange={(event) => setReason(event.target.value)} />
        )}
      </Field>
    </>
  );
  return { element, valid, payload: { effective, reason: reason.trim() || undefined } };
}

function useDeactivate(onDone: (result: BulkDeactivateResult) => void) {
  const { api } = useSession();
  return useMutation({
    mutationFn: async (body: {
      personIds: string[];
      effective: string;
      reason?: string;
    }): Promise<BulkDeactivateResult> => {
      const { data, error } = await api.POST('/api/v1/people/deactivate', {
        body: body as never,
      });
      if (error || !data) throw error ?? new Error('Uit dienst zetten mislukt');
      return data as BulkDeactivateResult;
    },
    onSuccess: onDone,
  });
}

/** De aangevinkte mensen uit dienst zetten. */
export function BulkDeactivateModal({
  personIds,
  onClose,
  onDone,
}: {
  personIds: string[];
  onClose: () => void;
  onDone: (result: BulkDeactivateResult) => void;
}) {
  const { t } = useTranslation();
  const text = useProblemText();
  const fields = useExitFields();
  const deactivate = useDeactivate(onDone);

  return (
    <Modal
      open
      onClose={onClose}
      testId="people-bulk-deactivate"
      title={t('peopleBulk.title', { count: personIds.length })}
      sub={t('peopleBulk.sub')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            variant="danger"
            testId="people-bulk-confirm"
            disabled={!fields.valid || deactivate.isPending}
            onClick={() => deactivate.mutate({ personIds, ...fields.payload })}
          >
            {t('peopleBulk.confirm', { count: personIds.length })}
          </Btn>
        </>
      }
    >
      <Stack>
        {fields.element}
        {deactivate.isError ? (
          <Note tone="danger" role="alert">
            {text(deactivate.error, t('errors.generic'))}
          </Note>
        ) : null}
      </Stack>
    </Modal>
  );
}

/**
 * Een uitdienstlijst: een CSV oplezen of nummers plakken, eerst zien wie het is en wie niet
 * gevonden werd, en pas dan uit dienst zetten.
 */
export function ExitListModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (result: BulkDeactivateResult) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const text = useProblemText();
  const fields = useExitFields();
  const [raw, setRaw] = useState('');
  const deactivate = useDeactivate(onDone);

  const match = useMutation({
    mutationFn: async (identifiers: string[]): Promise<MatchPeopleResult> => {
      const { data, error } = await api.POST('/api/v1/people/deactivate/match', {
        body: { identifiers } as never,
      });
      if (error || !data) throw error ?? new Error('Controleren mislukt');
      return data as MatchPeopleResult;
    },
  });

  const identifiers = parseExitList(raw);
  const found = match.data?.matched ?? [];
  const toDo = found.filter((hit) => hit.status === 'active');
  const already = found.filter((hit) => hit.status !== 'active');

  const readFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setRaw(await file.text());
    match.reset();
  };

  return (
    <Modal
      open
      wide
      onClose={onClose}
      testId="people-exit-list"
      title={t('peopleBulk.listTitle')}
      sub={t('peopleBulk.listSub')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          {match.data ? (
            <Btn
              variant="danger"
              testId="people-exit-confirm"
              disabled={toDo.length === 0 || !fields.valid || deactivate.isPending}
              onClick={() =>
                deactivate.mutate({ personIds: toDo.map((hit) => hit.personId), ...fields.payload })
              }
            >
              {t('peopleBulk.confirm', { count: toDo.length })}
            </Btn>
          ) : (
            <Btn
              testId="people-exit-check"
              disabled={identifiers.length === 0 || match.isPending}
              onClick={() => match.mutate(identifiers)}
            >
              {t('peopleBulk.check', { count: identifiers.length })}
            </Btn>
          )}
        </>
      }
    >
      <Stack>
        <Field label={t('peopleBulk.file')} hint={t('peopleBulk.fileHint')}>
          {(props) => (
            <input
              {...props}
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              data-testid="people-exit-file"
              onChange={(event) => void readFile(event)}
            />
          )}
        </Field>
        <Field label={t('peopleBulk.paste')}>
          {(props) => (
            <Textarea
              {...props}
              rows={5}
              value={raw}
              data-testid="people-exit-paste"
              onChange={(event) => {
                setRaw(event.target.value);
                match.reset();
              }}
            />
          )}
        </Field>

        {match.isError ? (
          <Note tone="danger" role="alert">
            {text(match.error, t('errors.generic'))}
          </Note>
        ) : null}

        {match.data ? (
          <div data-testid="people-exit-result">
            <Note tone={toDo.length > 0 ? 'info' : 'attention'}>
              {t('peopleBulk.found', { count: toDo.length })}
              {already.length > 0 ? ` ${t('peopleBulk.already', { count: already.length })}` : ''}
            </Note>
            {toDo.length > 0 ? (
              <ul className="a-muted" style={{ margin: '8px 0', paddingLeft: 18 }}>
                {toDo.slice(0, 50).map((hit) => (
                  <li key={hit.personId}>
                    {hit.name}
                    {hit.employeeNumber ? ` (${hit.employeeNumber})` : ''}
                  </li>
                ))}
                {toDo.length > 50 ? <li>…</li> : null}
              </ul>
            ) : null}
            {match.data.unmatched.length > 0 ? (
              <Note tone="attention" testId="people-exit-unmatched">
                {t('peopleBulk.unmatched', { count: match.data.unmatched.length })}{' '}
                {match.data.unmatched.slice(0, 20).join(', ')}
                {match.data.unmatched.length > 20 ? ' …' : ''}
              </Note>
            ) : null}
            {fields.element}
          </div>
        ) : null}

        {deactivate.isError ? (
          <Note tone="danger" role="alert">
            {text(deactivate.error, t('errors.generic'))}
          </Note>
        ) : null}
      </Stack>
    </Modal>
  );
}
